import React, { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { exchangeSession, claimBrowserForUser, afterSignInPath } from '../lib/authApi';
import { pendingReferralCode, forgetReferralCode, applyReferral } from '../lib/billingApi';
import { apiErrorMessage } from '../lib/projectApi';
import { WannaPublishLockup } from '../components/editor/WannaPublishLogo';

/**
 * Google sign-in return: Emergent Auth sends the browser back to <origin>/app#session_id=… (see startGoogleSignIn).
 * The session_id is exchanged once — by the backend — for the profile and the session cookie, then the URL fragment is
 * dropped and the editor opens. Rendered by AppRouter whenever the hash carries a session_id (before any route).
 */
export default function AuthCallback() {
  const location = useLocation();
  const navigate = useNavigate();
  const { setUser } = useAuth();
  const processed = useRef(false);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    if (processed.current) return;
    processed.current = true;
    const params = new URLSearchParams((location.hash || '').replace(/^#/, ''));
    const sessionId = params.get('session_id');
    (async () => {
      try {
        if (!sessionId) throw new Error('missing session id');
        const me = await exchangeSession(sessionId);
        // Google sign-up through a friend's invite link: the code remembered on the landing page goes on the account now
        // (the backend refuses it for anyone who already subscribed or was referred, so this is safe to try).
        const code = pendingReferralCode();
        if (code && me.referral && !me.referral.referred_by && !me.billing) {
          try { await applyReferral(code); me.referral = { ...me.referral, referred_by: true }; } catch { /* not eligible — nothing to do */ }
        }
        forgetReferralCode();
        await claimBrowserForUser(me.user_id);
        setUser(me);
        navigate(afterSignInPath(location.search), { replace: true });
      } catch (err) {
        setFailed(true);
        const message = await apiErrorMessage(err, 'Google sign-in did not complete — please try again.');
        navigate('/', { replace: true, state: { authError: message } });
      }
    })();
    // runs once on arrival (processed ref): the session id / return path are those of the URL we arrived with, and
    // navigate / setUser are stable — re-running on a later location change would re-exchange an already used session
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <main className="min-h-screen bg-[hsl(var(--wp-workspace))] text-foreground flex items-center justify-center" aria-busy={!failed} data-testid="auth-callback">
      <div className="flex flex-col items-center gap-3 text-sm text-muted-foreground">
        <WannaPublishLockup size={44} pulse />
        <span>{failed ? 'Returning to sign-in…' : 'Signing you in…'}</span>
      </div>
    </main>
  );
}
